<template>
  <header
    ref="headerRootRef"
    :class="{
      header: true,
      'header--plm': true,
      'header--search-would-reach-wordmark': searchWouldReachWordmark,
    }"
  >
    <div class="header__section">
      <fs-router-link
        :to="
          useUser().loggedIn
            ? { name: 'products', query: { view: useUserConfig().settings?.products?.latestView } }
            : { name: 'login' }
        "
      >
        <span
          :class="{
            'main-logo': true,
            'header__main-logo': true,
          }"
        >
          <span
            class="main-logo__mark"
            :title="`Release Version: ${releaseVersion}`"
            role="img"
            aria-label="Resourced"
          />
          <fs-tooltip v-if="testenvIdentifier" placement="bottom" class="main-logo__env-badge">
            TEST-{{ testenvIdentifier }}
          </fs-tooltip>
          <span v-else-if="isQA" class="main-logo__env-badge">QA</span>
          <fs-tooltip
            v-else-if="isLocal"
            :text="devBadgePath"
            :show="!!devBadgePath"
            placement="bottom"
            show-even-if-not-ellipsed
            class="main-logo__env-badge main-logo__env-badge--dev"
          >
            {{ devBadgeLabel }}
          </fs-tooltip>
        </span>
      </fs-router-link>
    </div>

    <div v-if="useUser().loggedIn" class="header__section">
      <product-search-field class="header__product-search-field" />

      <div class="nav-menu">
        <fs-router-link
          :class="{
            'nav-menu__item': true,
            'nav-menu__item--active': useRoute().name === 'products' || useRoute().name === 'product',
          }"
          :to="{ name: 'products', query: { view: useUserConfig().settings?.products?.latestView } }"
        >
          <span class="nav-menu__icon" v-html="productsIcon"></span>
          <span class="nav-menu__label"> Products </span>
        </fs-router-link>

        <fs-router-link
          :class="{ 'nav-menu__item': true, 'nav-menu__item--active': useRoute().name === 'components' }"
          :to="{ name: 'components' }"
        >
          <span class="nav-menu__icon nav-icon--components" v-html="componentsIcon"></span>
          <span class="nav-menu__label"> Components </span>
        </fs-router-link>

        <fs-router-link
          :class="{ 'nav-menu__item': true, 'nav-menu__item--active': useRoute().name === 'samples' }"
          :to="{ name: 'samples' }"
        >
          <span class="nav-menu__icon" v-html="samplesIcon"></span>
          <span class="nav-menu__label"> Samples </span>
        </fs-router-link>

        <router-link
          :class="{ 'nav-menu__item': true, 'nav-menu__item--active': useRoute().name === 'orders' }"
          :to="{ name: 'orders' }"
        >
          <span class="nav-menu__icon" v-html="ordersIcon"></span>
          <span class="nav-menu__label"> Orders </span>
        </router-link>
      </div>

      <!-- The four buttons shed their labels first and collapse into this menu
           only below 768, which is also where the season picker, the icons and
           the profile menu go, so nothing the narrow header hides becomes
           unreachable. -->
      <div class="nav-burger">
        <button
          ref="navBurgerRef"
          type="button"
          class="nav-burger__button"
          :class="{ 'nav-burger__button--open': navMenuIsOpened }"
          :aria-expanded="navMenuIsOpened"
          aria-label="Menu"
          @click="navMenuIsOpened = !navMenuIsOpened"
        >
          <span class="nav-burger__bars" aria-hidden="true"><span /><span /><span /></span>
          <!-- Once the bell and the chat icon are inside the menu, the burger is
               the only thing left that can say something is waiting. A dot, not
               a count: it stands for two different tallies at once. -->
          <span v-if="burgerHasAlerts" class="nav-burger__dot" aria-hidden="true"></span>
        </button>
      </div>
    </div>
    <div v-if="useUser().loggedIn" ref="headerRightRef" class="header__section">
      <div class="header__active-season-slot">
        <active-season-picker
          v-if="useUser().fullyLoaded"
          v-model="useActiveSeason().activeSeasonId"
          :seasons="useSeasons().loaded"
        />
      </div>

      <onboarding-progress-indicator />
      <user-menu-icons class="header__user-menu-icons" />

      <!-- TODO: figure out why do we need .stop modifier here
            because it blocks notification panel from close on click outside -->
      <div
        v-if="user"
        :class="{ 'nav-profile': true, 'is-active': profileMenuIsOpened }"
        @click="
          userMenu?.open($event);
          profileMenuIsOpened = true;
        "
      >
        <span class="nav-profile__name">
          {{ user.name }}
        </span>
        <span class="nav-profile__company">
          <!-- @vue-ignore useUser is hopelessly js -->
          {{ useUser().company?.name }}
        </span>
        <span v-if="!user.photo" class="nav-profile__photo" v-html="accountIcon"></span>
        <span v-if="user.photo" class="nav-profile__photo" :style="{ backgroundImage: 'url(' + userPhoto + ')' }">
          <img :src="userPhoto" :alt="user.name" />
        </span>
        <dropdown-caret class="nav-profile__chevron" :is-flipped="profileMenuIsOpened" />
      </div>

      <fs-popper
        ref="userMenu"
        placement="bottom-start"
        is-close-on-esc
        :offset="[0, 8]"
        @close="profileMenuIsOpened = false"
      >
        <fs-menu class="user-menu">
          <template v-if="plmCompanies.length > 1 && plmCompanies.length < 6">
            <fs-menu-group title="Switch brand">
              <fs-menu-item
                v-for="company in plmCompanies"
                :key="company.id"
                :label="company.name"
                :checked="company.brandID === useUser().brandId"
                :disabled="company.brandID === useUser().brandId || !company.brandID"
                @click="onBrandClick($event, company.brandID)"
                @mousedown="onBrandMousedown($event, company.brandID)"
              >
                <template #icon>
                  <img
                    v-if="company.brandID && !brandLogoErrored.has(company.brandID)"
                    class="brand-logo"
                    :src="`${blobUrl}/public/brand-logos/${company.brandID}.png`"
                    :alt="company.name"
                    @error="brandLogoErrored.add(company.brandID)"
                  />
                  <span v-else class="brand-logo brand-logo--fallback" v-html="officeBuildingIcon" />
                </template>
              </fs-menu-item>
            </fs-menu-group>
            <fs-menu-divider />
          </template>
          <template v-else-if="plmCompanies.length >= 6">
            <fs-menu-item label="Switch brand" :icon="officeBuildingIcon">
              <template #sub-menu>
                <fs-menu class="brand-switcher-submenu">
                  <fs-input
                    v-model="brandSearch"
                    class="brand-switcher-submenu__search"
                    placeholder="Search brands…"
                    is-search
                    auto-focus
                    :tabindex="0"
                    @click.stop
                    @keyup-enter="brandSearchEnter"
                  />
                  <div class="brand-switcher-submenu__list">
                    <fs-menu-item
                      v-for="company in filteredBrandCompanies"
                      :key="company.id"
                      :label="company.name || '—'"
                      :checked="company.brandID === useUser().brandId"
                      :disabled="company.brandID === useUser().brandId || !company.brandID"
                      tabindex="0"
                      @click="onBrandClick($event, company.brandID)"
                      @mousedown="onBrandMousedown($event, company.brandID)"
                      @keyup.enter="switchBrand(company.brandID)"
                    >
                      <template #icon>
                        <img
                          v-if="company.brandID && !brandLogoErrored.has(company.brandID)"
                          class="brand-logo"
                          :src="`${blobUrl}/public/brand-logos/${company.brandID}.png`"
                          :alt="company.name"
                          @error="brandLogoErrored.add(company.brandID)"
                        />
                        <span v-else class="brand-logo brand-logo--fallback" v-html="officeBuildingIcon" />
                      </template>
                    </fs-menu-item>
                    <div v-if="filteredBrandCompanies.length === 0" class="brand-switcher-submenu__empty">
                      No matches
                    </div>
                  </div>
                </fs-menu>
              </template>
            </fs-menu-item>
            <fs-menu-divider />
          </template>
          <fs-menu-item :to="{ name: 'colors' }" label="Colors" :icon="colorsIcon" />
          <fs-menu-item :to="{ name: 'measurement-charts' }" :label="measurementChartLabel" :icon="measurementIcon" />
          <fs-menu-item :to="{ name: 'suppliers' }" label="Suppliers" :icon="suppliersIcon" />
          <fs-menu-divider />
          <fs-menu-item v-if="showAdminLink" :to="{ name: 'admin' }" label="Admin" :icon="settingsIcon" />
          <fs-menu-divider v-if="showAdminLink" />
          <fs-menu-group title="Labs">
            <template v-for="labLink in labLinks" :key="labLink.label">
              <a v-if="labLink.url" :href="labLink.url" target="_blank">
                <fs-menu-item :label="labLink.label" :icon="labLink.icon" />
              </a>
            </template>
          </fs-menu-group>
          <fs-menu-divider />
          <!-- Who you are, shown here only when the bar is too narrow to carry
               it. The avatar is still in the bar directly above this menu, so
               only the two lines of text move. -->
          <div v-if="user" class="user-menu__identity">
            <span class="user-menu__identity-name">{{ user.name }}</span>
            <!-- @vue-ignore useUser is hopelessly js -->
            <span class="user-menu__identity-company">{{ useUser().company?.name }}</span>
          </div>
          <fs-menu-item label="Log out" :icon="signOutIcon" @click="logOut" />
        </fs-menu>
      </fs-popper>
    </div>

    <!-- The menu is a full-height sheet rather than a popper. A popper anchored
         to a button this close to the edge of a phone had nowhere to go — it
         rendered at x=0 and never faded in — and a sheet is what a phone menu
         wants to be anyway: everything the header shed, at a size you can hit. -->
    <teleport v-if="hasMounted" to="#portal-target">
      <div v-if="navMenuIsOpened" class="nav-sheet__scrim" aria-hidden="true" @click="navMenuIsOpened = false"></div>
      <aside
        ref="navSheetRef"
        class="nav-sheet"
        :class="{ 'nav-sheet--open': navMenuIsOpened }"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        tabindex="-1"
      >
        <header class="nav-sheet__top">
          <!-- The two panels live here rather than as rows in the list: they are
               the only entries that open something over the top of the sheet
               instead of navigating, and as icons they can carry their counts. -->
          <div class="nav-sheet__panels">
            <button type="button" class="nav-sheet__panel-btn" aria-label="Chat and tasks" @click="openChatFromMenu">
              <span class="nav-sheet__panel-icon" v-html="chatIcon"></span>
              <span v-if="useChat().unreadCount" class="nav-sheet__count">{{ useChat().unreadCount }}</span>
            </button>
            <button
              type="button"
              class="nav-sheet__panel-btn"
              aria-label="Notifications"
              @click="openNotificationsFromMenu"
            >
              <span class="nav-sheet__panel-icon" v-html="bellIcon"></span>
              <span v-if="useNotifications().numberOfUnreadNotifications" class="nav-sheet__count">
                {{ useNotifications().numberOfUnreadNotifications }}
              </span>
            </button>
          </div>
        </header>

        <div class="nav-sheet__body">
          <div v-if="useUser().fullyLoaded" class="nav-sheet__season" @click.stop>
            <active-season-picker v-model="useActiveSeason().activeSeasonId" :seasons="useSeasons().loaded" />
          </div>

          <div class="nav-sheet__group" @click="navMenuIsOpened = false">
            <fs-menu class="nav-sheet__menu">
              <fs-menu-item
                :class="{ 'nav-sheet__item--current': isCurrentPage('products', 'product') }"
                :to="{ name: 'products', query: { view: useUserConfig().settings?.products?.latestView } }"
                label="Products"
                :icon="productsIcon"
              />
              <fs-menu-item
                class="nav-icon--components"
                :class="{ 'nav-sheet__item--current': isCurrentPage('components') }"
                :to="{ name: 'components' }"
                label="Components"
                :icon="componentsIcon"
              />
              <fs-menu-item
                :class="{ 'nav-sheet__item--current': isCurrentPage('samples') }"
                :to="{ name: 'samples' }"
                label="Samples"
                :icon="samplesIcon"
              />
              <fs-menu-item
                :class="{ 'nav-sheet__item--current': isCurrentPage('orders') }"
                :to="{ name: 'orders' }"
                label="Orders"
                :icon="ordersIcon"
              />
            </fs-menu>
          </div>

          <div class="nav-sheet__group" @click="navMenuIsOpened = false">
            <fs-menu class="nav-sheet__menu">
              <fs-menu-item
                :class="{ 'nav-sheet__item--current': isCurrentPage('colors') }"
                :to="{ name: 'colors' }"
                label="Colors"
                :icon="colorsIcon"
              />
              <fs-menu-item
                :class="{ 'nav-sheet__item--current': isCurrentPage('measurement-charts') }"
                :to="{ name: 'measurement-charts' }"
                :label="measurementChartLabel"
                :icon="measurementIcon"
              />
              <fs-menu-item
                :class="{ 'nav-sheet__item--current': isCurrentPage('suppliers') }"
                :to="{ name: 'suppliers' }"
                label="Suppliers"
                :icon="suppliersIcon"
              />
              <fs-menu-item
                v-if="showAdminLink"
                :class="{ 'nav-sheet__item--current': isCurrentPage('admin') }"
                :to="{ name: 'admin' }"
                label="Admin"
                :icon="settingsIcon"
              />
              <a :href="helpPageUrl" target="_blank">
                <fs-menu-item label="Help" :icon="questionMarkIcon" />
              </a>
            </fs-menu>
          </div>

          <div class="nav-sheet__group">
            <fs-menu class="nav-sheet__menu">
              <fs-menu-group title="Labs">
                <template v-for="labLink in labLinks" :key="labLink.label">
                  <a v-if="labLink.url" :href="labLink.url" target="_blank">
                    <fs-menu-item :label="labLink.label" :icon="labLink.icon" />
                  </a>
                </template>
              </fs-menu-group>
            </fs-menu>
          </div>

          <!-- Flat, not the submenu the popper uses: a sheet is already the
               whole screen, so a nested panel has nowhere to open into. The
               list sits directly above the identity block it changes. -->
          <div v-if="plmCompanies.length > 1" class="nav-sheet__group">
            <fs-menu class="nav-sheet__menu">
              <fs-menu-group title="Switch brand">
                <fs-input
                  v-if="plmCompanies.length >= 6"
                  v-model="brandSearch"
                  class="nav-sheet__brand-search"
                  placeholder="Search brands…"
                  is-search
                  @keyup-enter="brandSearchEnter"
                />
                <div class="nav-sheet__brands">
                  <fs-menu-item
                    v-for="company in filteredBrandCompanies"
                    :key="company.id"
                    :label="company.name || '—'"
                    :checked="company.brandID === useUser().brandId"
                    :disabled="company.brandID === useUser().brandId || !company.brandID"
                    @click="onBrandClick($event, company.brandID)"
                    @mousedown="onBrandMousedown($event, company.brandID)"
                  >
                    <template #icon>
                      <img
                        v-if="company.brandID && !brandLogoErrored.has(company.brandID)"
                        class="brand-logo"
                        :src="`${blobUrl}/public/brand-logos/${company.brandID}.png`"
                        :alt="company.name"
                        @error="brandLogoErrored.add(company.brandID)"
                      />
                      <span v-else class="brand-logo brand-logo--fallback" v-html="officeBuildingIcon" />
                    </template>
                  </fs-menu-item>
                  <div v-if="filteredBrandCompanies.length === 0" class="nav-sheet__brands-empty">No matches</div>
                </div>
              </fs-menu-group>
            </fs-menu>
          </div>

          <!-- Who you are, and the way out — both at the bottom, where a phone
               menu puts the things you reach for last. -->
          <footer class="nav-sheet__foot">
            <div v-if="user" class="nav-sheet__user">
              <span
                v-if="user.photo"
                class="nav-sheet__avatar"
                :style="{ backgroundImage: 'url(' + userPhoto + ')' }"
              ></span>
              <span v-else class="nav-sheet__avatar" v-html="accountIcon"></span>
              <span class="nav-sheet__who">
                <span class="nav-sheet__name">{{ user.name }}</span>
                <!-- @vue-ignore useUser is hopelessly js -->
                <span class="nav-sheet__company">{{ useUser().company?.name }}</span>
              </span>
            </div>

            <button type="button" class="nav-sheet__logout" @click="logOut">
              <span class="nav-sheet__logout-icon" v-html="signOutIcon"></span>
              Log out
            </button>
          </footer>
        </div>
      </aside>
    </teleport>
  </header>
</template>

<script setup lang="ts">
import FsMenuGroup from '@/components/fs-menu/fs-menu-group.vue';
import { useActiveSeason } from '@/store/active-season';
import { useHelpPageUrl } from '@lib/help-link';
import { labUrl } from '@lib/lab-urls';
import { useMChartsLabel } from '@lib/mchart-label';
import { useSeasons } from '@/store/seasons';
import { useUserConfig } from '@/store/user-config';
import { useUser } from '@/store/user';
import accountIcon from '@assets/img/baseline-account_circle-24px.svg';
import DropdownCaret from '@components/dropdown-caret.vue';
import FsMenuDivider from '@components/fs-menu/fs-menu-divider.vue';
import FsMenuItem from '@components/fs-menu/fs-menu-item.vue';
import FsMenu from '@components/fs-menu/index.vue';
import FsRouterLink from '@components/fs-router-link.vue';
import FsInput from '@components/form-elements/fs-input.vue';
import FsPopper from '@components/poppers/fs-popper.vue';
import FsTooltip from '@components/poppers/fs-tooltip.vue';
import ProductSearchField from '@components/product-search-field.vue';
import { navIcons } from '@lib/nav-icons';
import signOutIcon from '@icons/other/sign-out.svg';
import { intercomShutdown } from '@lib/intercom';
import { blobUrl, useApi } from '@lib/api.ts';
import { onBrandClick, onBrandMousedown, switchBrand, usePlmBrands } from '@lib/brand-switch';
import { purgeAll as purgeAllSnapshots } from '@lib/snapshot-cache';
import { imagePath } from '@lib/imgproxy';
import ActiveSeasonPicker from '@pages/frame/navbar/active-season-picker.vue';
import OnboardingProgressIndicator from '@pages/frame/navbar/onboarding-progress-indicator.vue';
import { computed, nextTick, onMounted, onScopeDispose, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { createShortcutLayer } from '@lib/shortcuts';
import { lockWindowScroll, unlockWindowScroll } from '@lib/window-scrollbar';
import { chatPanel } from '@lib/chat-panel';
import { notificationsPanel } from '@lib/notifications-panel';
import { useChat } from '@/store/chat';
import { useNotifications } from '@/store/notifications';
import bellIcon from '@icons/other2/bell.svg';
import chatIcon from '@icons/filters-icons/comment-outlined.svg';
import questionMarkIcon from '@icons/other/question-mark-circle.svg';
import { useRoute } from 'vue-router';
import UserMenuIcons from './user-menu-icons.vue';
import labelIcons from '@icons/filters-icons/tags.svg';
import tableIcon from '@icons/other3/table-layout.svg';
import imageIcon from '@icons/filters-icons/image.svg';
import analyticsIcon from '@icons/other2/graph-curve.svg';
import sessionsIcon from '@icons/sample-handling/review-event.svg';
import { canSeeAdmin } from '@lib/admin-nav';

// The destinations the palette and the admin nav also list, drawn from the one shared set.
const {
  brand: officeBuildingIcon,
  suppliers: suppliersIcon,
  colors: colorsIcon,
  products: productsIcon,
  measurements: measurementIcon,
  orders: ordersIcon,
  components: componentsIcon,
  samples: samplesIcon,
  admin: settingsIcon,
} = navIcons;

const isQA = ref(false);
const isLocal = ref(false);
const devBadgeLabel = import.meta.env.VITE_DEVENV_IDENTIFIER ? `DEV-${import.meta.env.VITE_DEVENV_IDENTIFIER}` : 'DEV';
const testenvIdentifier = import.meta.env.VITE_TESTENV_IDENTIFIER;
const devBadgePath = import.meta.env.VITE_DEVENV_PATH ?? '';
const profileMenuIsOpened = ref(false);
const navMenuIsOpened = ref(false);
// #portal-target only exists after the frame has mounted.
const hasMounted = ref(false);
onMounted(() => (hasMounted.value = true));

// Whether an open search field would reach the wordmark.
//
// This cannot be a media query. The field is right-anchored, so its open left
// edge is (its right edge - its open width), and its right edge is set by where
// the middle header section starts — which follows the RIGHT section's content
// width, not the viewport. Measured: the middle section starts at 334px on a
// 1440 viewport but at 318px on a 1600 one, so the clash is not monotonic in
// width and no breakpoint states it correctly. Measure it instead.
//
// The field's right edge is the same open or closed (that is the point of
// anchoring it), so this is answerable at any time without knowing whether the
// search is open. CSS handles the "while open" half via :has().
const headerRootRef = useTemplateRef<HTMLElement>('headerRootRef');
const headerRightRef = useTemplateRef<HTMLElement>('headerRightRef');
const searchWouldReachWordmark = ref(false);

// A little air, so the words never sit flush against the field's edge.
const WORDMARK_CLEARANCE_PX = 12;

/** The field's open width, declared as --search-open-width in product-search-field.vue. */
function openWidthPx(field: Element, rootFontSize: number) {
  const declared = getComputedStyle(field).getPropertyValue('--search-open-width').trim();
  const value = parseFloat(declared);
  if (!Number.isFinite(value)) return 0;
  return declared.endsWith('rem') ? value * rootFontSize : value;
}

function measureSearchClash() {
  const root = headerRootRef.value;
  if (!root) return;
  const mark = root.querySelector('.main-logo__mark');
  const field = root.querySelector('.product-search-field');
  if (!mark || !field) return;

  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
  const openLeftEdge = field.getBoundingClientRect().right - openWidthPx(field, rootFontSize);

  searchWouldReachWordmark.value = openLeftEdge < mark.getBoundingClientRect().right + WORDMARK_CLEARANCE_PX;
}

let headerResizeObserver: ResizeObserver | undefined;

function onHeaderResize() {
  measureSearchClash();
  closeSheetIfBurgerGone();
}

onMounted(() => {
  headerResizeObserver = new ResizeObserver(onHeaderResize);
  if (headerRootRef.value) headerResizeObserver.observe(headerRootRef.value);
  if (headerRightRef.value) headerResizeObserver.observe(headerRightRef.value);
  measureSearchClash();
  // The lockup is a masked SVG, so it is sized by CSS rather than by a load —
  // but the right section carries text whose width settles with the webfont.
  document.fonts?.ready.then(() => measureSearchClash());
});
onUnmounted(() => headerResizeObserver?.disconnect());

// The header is fixed and full-width, so its own box never resizes — watching it
// alone would answer this once and never again. What actually moves the field's
// right edge is the right section: it does not exist until the user resolves
// (the navbar renders before that), and once it does its content keeps changing
// width as the season pill and the onboarding ring come and go.
watch(
  headerRightRef,
  (section, previous) => {
    if (previous) headerResizeObserver?.unobserve(previous);
    if (section) headerResizeObserver?.observe(section);
    measureSearchClash();
  },
  { flush: 'post' }
);

/** One dot for two tallies — unread chat, or unread notifications. */
const burgerHasAlerts = computed(() => useChat().unreadCount > 0 || useNotifications().numberOfUnreadNotifications > 0);

// The sheet covers the whole viewport at these widths, so it has to go before
// the panel it opened can be seen. Closing the chat panel or marking the
// notifications read is the business of the watchers in user-menu-icons, which
// treat these opens exactly like a click on the bar's own icons.
const openChatFromMenu = () => {
  navMenuIsOpened.value = false;
  chatPanel.open();
};
const openNotificationsFromMenu = () => {
  navMenuIsOpened.value = false;
  notificationsPanel.isOpen.value = true;
};

// Not useShortcutLayer: that provide()s the layer to the whole navbar subtree, so
// user-menu-icons' Alt+C would attach to it and only work while the sheet is open.
// The sheet has no descendants of its own registering shortcuts, so it keeps the
// layer to itself.
const navSheetShortcuts = createShortcutLayer('Menu');
navSheetShortcuts.register('Escape', () => (navMenuIsOpened.value = false), { description: 'Close menu' });
onScopeDispose(() => navSheetShortcuts.destroy());

const navSheetRef = useTemplateRef<HTMLElement>('navSheetRef');
const navBurgerRef = useTemplateRef<HTMLButtonElement>('navBurgerRef');

// The sheet covers the viewport, so the page behind it must not scroll under the
// thumb, and focus has to follow the thing that just opened — then come back to
// the burger, which is where the eye already is.
watch(navMenuIsOpened, async (isOpen) => {
  if (!isOpen) {
    navSheetShortcuts.deactivate();
    unlockWindowScroll();
    navBurgerRef.value?.focus();
    return;
  }

  navSheetShortcuts.activate();
  lockWindowScroll();
  await nextTick();
  // A close during that tick has already moved focus to the burger.
  if (navMenuIsOpened.value) navSheetRef.value?.focus();
});

// The burger is the sheet's only control in the bar, and CSS hides it above the
// mobile band — a rotation or a drag of the window edge while the sheet is open
// would otherwise leave a full-viewport overlay over a locked page. Asking the
// burger whether it is displayed rather than re-stating its breakpoint here
// keeps the two in step, including at the fractional widths where neither side
// of a min/max pair matches.
function closeSheetIfBurgerGone() {
  if (!navMenuIsOpened.value) return;
  if (navBurgerRef.value?.offsetParent === null) navMenuIsOpened.value = false;
}

onUnmounted(() => {
  if (navMenuIsOpened.value) unlockWindowScroll();
});

const userMenu = useTemplateRef<any>('userMenu');
const brandSearch = ref('');
const brandLogoErrored = ref(new Set<number>());
const plmCompanies = usePlmBrands();
const filteredBrandCompanies = computed(() => {
  const q = brandSearch.value.trim().toLowerCase();
  if (!q) return plmCompanies.value;
  return plmCompanies.value.filter((c: any) => (c.name ?? '').toLowerCase().includes(q));
});

const releaseVersion = import.meta.env.VITE_RELEASE_TAG;

const measurementChartLabel = useMChartsLabel();

// The sheet replaces the bar below 768, so it marks the current page by route name.
// Prefix rather than equality: 'product' is a redirect-only route whose real
// destinations are its children ('product-articles', 'product-bom', …), so an exact
// match would never light the Products row on a product page.
function isCurrentPage(...routeNames: string[]) {
  const current = String(useRoute().name);
  return routeNames.some((name) => current === name || current.startsWith(`${name}-`));
}
const helpPageUrl = useHelpPageUrl();

const showAdminLink = computed(() => canSeeAdmin());

const user = computed<any | null>(() => useUser().current);
const userPhoto = computed(() => imagePath(`images/${user.value?.photo}`, 500, 300));

function withBrandParam(url: string | undefined): string | undefined {
  if (!url) return url;
  // Only seed brandId when the user has access to more than one brand — otherwise
  // the labs app's own default is fine and we avoid pinning single-brand users.
  if (plmCompanies.value.length <= 1) return url;
  const brandId = useUser().brandId;
  if (!brandId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}brandId=${brandId}`;
}
// Resolved against the host this page is served from, so a preview environment links to its own
// labs rather than QA's. An environment with no labs resolves to nothing and renders no links,
// which is why this filters rather than rendering a dead entry.
const labLinks = computed(() =>
  (
    [
      { lab: 'rangeplan', label: 'Range Plan', icon: tableIcon },
      { lab: 'canvas', label: 'Studio', icon: imageIcon },
      { lab: 'planalytics', label: 'Planalytics', icon: analyticsIcon },
      { lab: 'labels', label: 'Labels', icon: labelIcons },
      { lab: 'sessions', label: 'Sessions', icon: sessionsIcon },
    ] as const
  )
    .map(({ lab, label, icon }) => ({ url: withBrandParam(labUrl(lab)), label, icon }))
    .filter((link) => link.url)
);
const api = useApi();
async function logOut() {
  // Clear Intercom's session cookies before ours, so the next user on this
  // browser can't see this user's conversations.
  intercomShutdown();
  // Don't leave brand data at rest on (possibly shared) machines
  await purgeAllSnapshots();
  await api.account_Logout();
  window.location.assign('/plm/login');
}
function brandSearchEnter() {
  const q = brandSearch.value.trim().toLowerCase();
  const candidates = filteredBrandCompanies.value.filter((c: any) => c.brandID && c.brandID !== useUser().brandId);
  const exact = candidates.find((c: any) => (c.name ?? '').toLowerCase() === q);
  const target = exact ?? candidates[0];
  if (target) switchBrand(target.brandID);
}

onMounted(() => {
  isQA.value = window.location?.href.includes('qa');
  isLocal.value = window.location?.href.includes('localhost');
});
</script>

<style lang="scss" scoped>
@import '@styles/variables';
@import '@styles/media-queries';
@import '@styles/text';
@import '@styles/app-header';

$dropdown-icon-size: 2.8rem;

// Nav icon fill correction ----------------------------------------------------
//
// Three of these four fill their own viewBox; components/button.svg leaves a
// margin inside its, and the artwork covers 78% of the box it is drawn in. CSS
// sizes the viewBox, not the artwork, so an equal box gives unequal icons — the
// longest ink side ran from 14.9px to 20px across the row, and in the sheet
// these four sat 3px smaller than every other item.
//
// Multiplying by 1/that share makes it fill its own box, so whatever box it is
// given governs its size. The factor describes the artwork rather than the place
// it is used, which is why the one value serves both the nav row and the sheet.
//
// Transform rather than size, so layout and centring are untouched. Re-measure
// when the icon is swapped, and keep this in step with `componentsIconScale` in
// lib/nav-icons.ts, which every other place drawing it at a shared box uses.
// Icons owned by the header are cropped at source instead — see box.svg's viewBox.
.nav-icon--components :deep(svg) {
  transform: scale(1.277);
}

.user-menu {
  width: 20rem;

  // The name and the brand, for the widths where the bar has dropped them.
  //
  // Hidden by default and shown below $desktop-l-width, which is deliberately
  // the same threshold .nav-profile uses to hide __name and __company — the two
  // rules are one decision, so they have to move together. In the burger band
  // this menu does not exist at all; the sheet's footer carries the same two
  // lines above its own Log out row.
  &__identity {
    display: none;
  }

  @media (max-width: #{$desktop-l-width - 1px}) {
    &__identity {
      display: flex;
      flex-direction: column;
      min-width: 0;
      // The rule underneath is the menu's own separator rather than a border on
      // a box: same hairline and the same 0.8rem bleed past the wrapper padding
      // that fs-menu-divider uses, so Log out sits in its own section like every
      // other group here. The side padding absorbs the negative margin so the
      // text still lines up with the item labels, which are indented past their
      // icons.
      width: calc(100% + 1.6rem);
      margin: 0 -0.8rem 0.7rem;
      padding: 0.6rem 2.4rem 0.8rem;
      border-bottom: 1px solid rgba($c-dark, 0.1);
    }

    &__identity-name {
      @include body;
      color: $c-ink;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__identity-company {
      @include caption--small;
      text-transform: uppercase;
      color: $c-ink-soft;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

// The mobile menu. Teleported out with the sheet, so it cannot be nested under
// .header--plm — the descendant selector would never reach it.
// The navbar's own height, which the sheet and its scrim both start from: they
// begin exactly where the bar ends. The burger only exists below 768, so the bar
// under them is always the short one.
$navbar-height-mobile: 5.6rem;

.nav-sheet {
  position: fixed;
  // Below the navbar and starting under it, like the chat panel. That is what
  // lets the burger stay visible and tappable while the sheet is open — one
  // control in one place that morphs between the two states, instead of a
  // burger here and a close button over there that the thumb has to travel to.
  inset: $navbar-height-mobile 0 0 auto;
  z-index: $zi-side-panel;
  display: flex;
  flex-direction: column;
  width: 100vw;
  background: $c-surface;
  box-shadow: -1.2rem 0 3.2rem rgba($c-ink, 0.18);
  // Off to the right until opened. Kept in the DOM so the slide has something
  // to animate from; visibility keeps it off the tab order while closed.
  transform: translateX(100%);
  visibility: hidden;
  transition:
    transform $transition-smooth $ease-out-soft,
    visibility 0s linear $transition-smooth;

  &--open {
    transform: translateX(0);
    visibility: visible;
    transition:
      transform $transition-smooth $ease-out-soft,
      visibility 0s;
  }

  &__scrim {
    position: fixed;
    // Starts below the bar, not at inset: 0. The bar is solid so it will not
    // tint, but it should not sit behind a dimming layer either: it stays fully
    // lit because the burger in it is the control that closes what it opened.
    inset: $navbar-height-mobile 0 0 0;
    // Under the sheet, and under the navbar with it — the bar has to stay clear
    // and clickable so the burger can close what it opened.
    z-index: $zi-side-panel - 1;
    background: rgba($c-ink, 0.4);
  }

  &__top {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 5.6rem;
    padding: 0 0.8rem;
    border-bottom: 1px solid $c-hairline;
  }

  &__panels {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  &__panel-btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 4.4rem;
    height: 4.4rem;
    padding: 0;
    border: none;
    border-radius: $br-pill;
    background: none;
    color: $c-ink;
    cursor: pointer;

    &:hover {
      background: $c-surface-muted;
    }
  }

  &__panel-icon {
    display: flex;

    :deep(svg) {
      width: 2.2rem;
      height: 2.2rem;
      fill: currentcolor;
    }
  }

  &__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    // Tight enough that the whole menu lands inside a small phone without
    // scrolling — 14 rows, an identity block and the season picker have to fit
    // between the top bar and the bottom of the screen.
    padding: 0 0 1.2rem;
  }

  &__foot {
    margin-top: auto;
    padding-top: 0.8rem;
    border-top: 1px solid $c-hairline;
  }

  &__logout {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    width: 100%;
    padding: 1rem 2rem 1.6rem;
    border: none;
    background: none;
    font-family: inherit;
    font-size: 1.5rem;
    color: $c-ink;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: $c-surface-muted;
    }
  }

  &__logout-icon {
    display: flex;

    :deep(svg) {
      width: 1.8rem;
      height: 1.8rem;
      fill: currentcolor;
    }
  }

  &__user {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    padding: 1rem 2rem;
  }

  &__avatar {
    flex: none;
    width: 3.6rem;
    height: 3.6rem;
    border-radius: 50%;
    background-color: $c-surface-muted;
    background-size: cover;
    background-position: center;

    :deep(svg) {
      width: 100%;
      height: 100%;
      fill: $c-ink-soft;
    }
  }

  &__who {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    @include body;
    color: $c-ink;
  }

  &__company {
    @include caption--small;
    text-transform: uppercase;
    color: $c-ink-soft;
  }

  // The picker sizes itself to its content; without this it stretches to the
  // full width of the sheet and reads as a field rather than a chip.
  &__season {
    display: flex;
    padding: 1rem 1.6rem;

    :deep(.active-season-picker) {
      flex: none;
    }
  }

  // Each block is separated by a rule and real space, so the sheet reads as
  // sections rather than one long column of links.
  &__group {
    padding: 0.4rem 0.8rem;
    border-top: 1px solid $c-hairline;
  }

  // The menu items ship at popper scale; on a phone sheet they are the whole
  // interface, so they take the body size the rest of the app uses.
  &__menu {
    width: 100%;

    :deep(.fs-menu-item) {
      min-height: 3.6rem;
    }
    // The app's body face and rhythm, inherited rather than re-declared — the
    // items ship at popper scale, and on a phone sheet they are the interface.
    :deep(.fs-menu-item__label) {
      @include body;
      font-size: 1.5rem;
    }
  }

  // The bar's selected pill, on a full-width row. Same fill, same lift, so the
  // sheet and the bar agree about what "you are here" looks like.
  :deep(.nav-sheet__item--current) {
    background-color: $c-ink;
    color: $c-surface;
    box-shadow: $shadow-pill;

    // Ties with fs-menu-item's own hover on specificity, so without this the
    // winner is decided by stylesheet order.
    &:hover {
      background-color: $c-ink;
    }
  }

  &__brand-search {
    margin: 0.4rem 0.4rem 0.8rem;
  }

  // Long enough to show a handful of brands, short enough that the identity
  // block and the way out stay in reach below it.
  &__brands {
    max-height: 24rem;
    overflow-y: auto;
  }

  &__brands-empty {
    padding: 0.8rem;
    text-align: center;
    color: $c-ink-soft;
    font-size: 1.3rem;
  }

  &__count {
    position: absolute;
    top: 0.4rem;
    right: 0.4rem;
    min-width: 1.8rem;
    padding: 0 0.5rem;
    border-radius: $br-pill;
    background: $c-accent;
    color: $c-on-accent;
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1.8rem;
    text-align: center;
  }
}

// Teleported out of the header with the popper, so these cannot be nested under
// .header--plm — the descendant selector would never match. Same reason
// .user-menu sits out here.

.brand-logo {
  display: block;
  height: 1.4rem;
  width: 1.4rem;
  margin-right: 1.2rem;
  flex-shrink: 0;
  object-fit: contain;

  &--fallback :deep(svg) {
    height: 100%;
    width: 100%;
    fill: $c-dark;
  }
}

.brand-switcher-submenu {
  width: 24rem;

  &__search {
    margin: 0.4rem 0.4rem 0.6rem;
  }

  &__list {
    max-height: 36rem;
    overflow-y: auto;
  }

  &__empty {
    padding: 0.8rem;
    text-align: center;
    color: rgba($c-dark, 0.5);
    font-size: 1.2rem;
  }
}

@mixin logo-symbol-only {
  // Symbol aspect ratio is 1080 / 739.02 ≈ 1.461, so at 2.6rem tall the width
  // is 3.8rem.
  width: 3.8rem;
  height: 2.6rem;
  -webkit-mask-image: url('@/assets/img/resourced-mark.svg');
  mask-image: url('@/assets/img/resourced-mark.svg');
  -webkit-mask-size: 3.8rem 2.6rem;
  mask-size: 3.8rem 2.6rem;
}

// Same result, none of the reflow. The mark is a mask over a solid fill, already
// anchored left with no-repeat, so swapping in the symbol mask paints the symbol
// at the left and masks the words away while the element keeps its full lockup
// width. Use this where the lockup has to disappear WITHOUT handing its space to
// the rest of the row.
@mixin logo-symbol-only-in-place {
  -webkit-mask-image: url('@/assets/img/resourced-mark.svg');
  mask-image: url('@/assets/img/resourced-mark.svg');
  -webkit-mask-size: 3.8rem 2.4rem;
  mask-size: 3.8rem 2.4rem;
}

.header {
  @mixin short-logo {
    .fs-logo__part2,
    .fs-logo__part1 {
      opacity: 0;
    }

    .fs-logo__f,
    .fs-logo__s {
      opacity: 1;
    }

    .fs-logo__s {
      transform: translateX(-4.4rem);
    }
  }

  @include app-header-bar;

  &__active-season-slot {
    display: flex;
    align-items: center;
    height: 100%;
    margin-right: 2rem;
  }

  &__product-search-field {
    margin-right: 1.3rem;
  }

  &__new-product-but {
    margin-right: 1.5rem;
    width: 18rem;
  }

  &__user-menu-icons {
    height: 100%;
    margin-right: 4.2rem !important;
  }
}

// Main logo ------------------------------------------------------------------
.main-logo {
  @include app-header-logo;
}

.header--plm {
  @include app-header-surface;
  @include app-header-nav-menu;
  @include app-header-profile;

  // The season slot reserves the picker's FILLED width at all times, so the
  // picker's select/clear animation plays out inside the slot (its pill grows
  // rightward into the reserved space) and the header's flex distribution
  // never changes — the nav, icons and profile all stay perfectly still.
  // The reservation is outside the pill's border, so the empty state shows a
  // compact icon pill with invisible slack instead of in-pill white space.
  // Desktop only: below that the slot is display:none.
  @include desktop-min {
    .header__active-season-slot {
      width: 17.2rem;
      justify-content: flex-start;
    }
  }

  // Hidden until the nav collapses into it — see the mobile-only block.
  .nav-burger {
    display: none;

    &__button {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 3.6rem;
      height: 3.6rem;
      padding: 0;
      border: none;
      border-radius: $br-pill;
      background: none;
      color: $c-ink;
      cursor: pointer;
      transition: background-color $transition-fast ease;

      &:hover,
      &--open {
        background: $c-surface-muted;
      }
    }

    &__dot {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      width: 0.8rem;
      height: 0.8rem;
      border-radius: 50%;
      background: $c-accent;
      box-shadow: 0 0 0 2px $c-surface;
    }

    // Three pill bars that fold into a cross.
    //
    // Plain SCSS values, not CSS custom properties inside calc(): `-` and `/`
    // are SCSS operators, so `calc((var(--a) - var(--b)) / 2)` gets rewritten
    // at compile time into something the browser drops on the floor — the rule
    // matches, and none of its declarations apply.
    $burger-box: 2.6rem;
    $burger-bar: 0.5rem;
    $burger-travel: ($burger-box - $burger-bar) * 0.5;

    &__bars {
      position: relative;
      width: $burger-box;
      height: $burger-box;

      span {
        position: absolute;
        left: 0;
        width: 100%;
        height: $burger-bar;
        // Radius equal to the full height, so the ends are true half-circles
        // and stay that way through the rotation — the cross is the same three
        // bars at the same weight, not a second, thinner glyph.
        border-radius: $burger-bar;
        background: currentcolor;
        transition:
          transform $transition-smooth $ease-out-soft,
          opacity $transition-fast ease;
      }
      span:nth-child(1) {
        top: 0;
      }
      span:nth-child(2) {
        top: $burger-travel;
        // Closing: the middle bar waits for the other two to finish rotating
        // before it fades back, so neither direction passes through a frame
        // that reads as three bars crossing. Opening, it leaves immediately —
        // the delay is dropped in --open below. Order matches the transition
        // property list above: transform, then opacity.
        transition-delay: 0s, $transition-smooth;
      }
      span:nth-child(3) {
        bottom: 0;
      }
    }

    &__button--open .nav-burger__bars {
      span:nth-child(1) {
        transform: translateY($burger-travel) rotate(45deg);
      }
      span:nth-child(2) {
        opacity: 0;
        transition-delay: 0s, 0s;
      }
      span:nth-child(3) {
        transform: translateY(-$burger-travel) rotate(-45deg);
      }
    }
  }

  // Mobile / narrow-viewport adjustments. The full wordmark holds down to
  // ~570px and then swaps to the icon-only mark, which stays visible at the
  // narrowest widths. Nav labels collapse to icons below 1024px and the help
  // icon is dropped below 480px.
  @include desktop-max {
    // The logo and the avatar are the two things that touch the edges, and at
    // 0.8rem they were 7px off the glass at 823px wide. In px, not rem: the
    // root font scales down with the viewport here, so a rem gutter shrinks
    // fastest exactly where the crowding is worst.
    padding: 0 clamp(16px, 2.4vw, 28px);

    .header__section {
      min-width: 0;
      gap: 0.4rem;
    }

    // The badge costs the logo ~60px, enough to push the nav under the season
    // chip at 1028. QA and local pay for it in labels; production is untouched.
    &:has(.main-logo__env-badge) {
      .nav-menu__label {
        display: none;
      }

      .nav-menu__item {
        width: 4rem;
        padding: 0;
        justify-content: center;
      }
    }

    .header__product-search-field {
      margin-right: 1.2rem;
    }

    .header__user-menu-icons {
      margin-right: 0.4rem !important;
    }

    // The icon box shrinks with the rest of the row here; the svg fills it, as
    // on desktop, so the four stay equal to each other on the way down.
    .nav-menu__icon :deep(svg) {
      width: 100%;
      height: 100%;
    }
  }

  // Below 1024 the four buttons collapse into the burger rather than into four
  // unlabelled glyphs. Icons alone were the previous answer and it cost the
  // labels at exactly the width where they are hardest to do without.
  //
  // 1024 rather than 768 because the season picker sits in this row too: with
  // both, the header overflows at 780 and pushes the avatar off the right edge.
  // The burger is where the picker goes, so the swap has to happen while there
  // is still room for the pair.
  // The full desktop layout — labelled nav, the season picker's reserved slot,
  // and the profile's name and company — needs about 1390px. On a 1280 laptop
  // it overflowed the viewport by 107px with the search still closed, which is
  // what jammed the season pill and pushed the avatar off the end.
  //
  // So it condenses here rather than at 1200: the picker drops its reserved
  // width and the profile drops to its avatar, which is ~210px back.
  @media (max-width: #{$desktop-l-width - 1px}) {
    .header__active-season-slot {
      width: auto;
      margin-right: 0.4rem;
    }

    .nav-profile {
      // Keep the chevron — it's the affordance that tells the user the avatar
      // is a dropdown trigger. Pull the right padding in to make room for it.
      padding: 0.4rem 1.8rem 0.4rem 0.4rem;
      grid-template-columns: auto;
      grid-template-rows: auto;
      grid-template-areas: 'photo';

      &__name,
      &__company {
        display: none;
      }
      &__photo {
        margin-left: 0;
        height: 3.6rem;
        width: 3.6rem;
      }
      &__chevron {
        width: 1.6rem;
      }
    }
  }

  // Open-ended rather than stopping at 768: a viewport can land on 767.33, where
  // a 768..1023 band and a ..767 band both miss. The burger block below hides
  // this menu anyway, so overlapping them leaves no width unclaimed.
  @include mobile-to-tablet {
    .nav-menu__item {
      width: 4rem;
      padding: 0;
      justify-content: center;
    }

    .nav-menu__label {
      display: none;
    }
  }

  @include mobile-only {
    .nav-menu {
      display: none;
    }

    .nav-burger {
      display: block;
    }

    // The burger carries it from here down.
    .header__active-season-slot {
      display: none;
    }
  }

  // Wherever the burger shows, the bar is the mobile one: logo, search and the
  // menu, nothing else. The icons and the profile are reachable from inside the
  // sheet instead.
  //
  // Same band as the burger above, deliberately. Split them and the band between
  // shows the burger inside the desktop three-section layout, with the search
  // marooned mid-bar while the icons and avatar still hold the right edge.
  @include mobile-only {
    .header__user-menu-icons,
    .nav-profile {
      display: none;
    }

    // With the right-hand section emptied, the search and the burger claim the
    // right edge together — the margin goes on the first of the pair so they
    // travel as one, rather than the search staying marooned mid-bar.
    .header__product-search-field {
      margin-left: auto;
      margin-right: 0.4rem !important;
    }

    // …and the middle section has to be able to reach that edge to put it there.
    .header__section:nth-child(2) {
      flex: 1;
      min-width: 0;
    }

    // With only three things left in the bar, they should read as one row of
    // equals. 2.6rem is the logo mark's height at this width.
    .nav-burger__button {
      width: 4rem;
      height: 4rem;
    }

    // The field and its open-button have to be the same height, or the button —
    // which is absolutely positioned to the field's top-left — hangs below it
    // and the glyph reads 2px low against the logo and the burger.
    .header__product-search-field {
      height: 4rem;

      :deep(.product-search-field__open-button) {
        width: 4rem;
        height: 4rem;

        svg {
          width: 2.2rem;
          height: 2.2rem;
        }
      }

      // The input fills the field, so its text centres with everything else.
      :deep(.product-search-field__field-itself),
      :deep(.fs-input__text-input-label),
      :deep(input) {
        height: 100%;
      }
    }
  }

  // At narrow widths the full lockup is impractical — fall back to the
  // logo symbol only (per the guidelines: the logo is the shorthand
  // identifier for use where the wordmark doesn't fit). Symbol aspect ratio
  // is 1080 / 739.02 ≈ 1.461, so at 2.6rem tall the width is 3.8rem.
  //
  // This one is unconditional: below 569px the wordmark never fits at all. The
  // other reason to drop it — an open search field reaching it — is measured
  // rather than guessed at; see header--search-would-reach-wordmark below.
  @media (max-width: 569px) {
    .main-logo {
      gap: 0.4rem;
    }
    .main-logo__mark {
      @include logo-symbol-only;
    }
  }

  // The open field reaches the lockup on real laptop widths, not just narrow
  // ones: measured at 1300 it lands 69px inside it. The words go and the symbol
  // stays, which is the guideline's own fallback for where the lockup does not
  // fit.
  //
  // In place, though. The field is absolutely positioned and right-anchored, so
  // it grows over the lockup rather than needing its space — and collapsing the
  // lockup for real handed 109px back to the row and slid the nav 54px left on
  // every open. Hiding the ink alone keeps the whole row still.
  //
  // Only when it actually would. searchWouldReachWordmark is measured in script
  // — see the comment there for why a breakpoint cannot express this — and this
  // supplies the other half of the condition: while the field is open.
  // &.header--… not &--…: this block is nested inside .header--plm, so a bare
  // &--suffix would compile to .header--plm--search-would-reach-wordmark.
  &.header--search-would-reach-wordmark:has(.product-search-field--opened) {
    .main-logo__mark {
      @include logo-symbol-only-in-place;
    }
  }

  @include mobile-xs-only {
    // At very narrow widths (< 480px) shrink the row so the logo, search and
    // burger fit comfortably down to ~300px. The gutter holds at a flat 16px:
    // the logo and avatar sitting on the glass is what this was fixing, and it
    // is worst at the narrowest widths.
    padding: 0 16px;

    .nav-menu__item {
      padding: 0 0.3rem 0.4rem 0.3rem;
    }
    .nav-menu__icon {
      height: 2.4rem;
      width: 2.4rem;
    }

    .header__product-search-field {
      margin-right: 0.6rem;
    }

    .nav-profile {
      padding: 0.2rem 1.6rem 0.2rem 0.2rem;

      &__photo {
        height: 3rem;
        width: 3rem;
      }
      &__chevron {
        width: 1.4rem;
      }
    }
  }
}
</style>
